import React, { useEffect, useMemo, useState } from 'react'
import { Button, Drawer, Popover, useMediaQuery, useTheme } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import CheckIcon from '@mui/icons-material/Check'
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined'
import Text from '../../Text'
import CircularProgressCustom from '../../CircularProgressCustom'
import hipersendService from '../../../services/hipersend'
import { agruparMotivos } from '../../../libs/relatorioMotivos'
import { daysBetween } from '../../shared/report/periodRange'

const fmt = (n) => Number(n || 0).toLocaleString('pt-BR')

// Carregado só no clique: a pdfmake não entra no pacote do painel.
const carregarGerador = () => import('../../../libs/relatorioPdf')

const OPCOES = {
  erros: {
    titulo: 'Mensagens por tipo de erro',
    descricao: 'Resumo para o gestor, com o que significa cada falha e o que fazer, mais a lista das mensagens.',
    unidade: ['mensagem', 'mensagens'],
  },
  inexistentes: {
    titulo: 'Números sem WhatsApp',
    descricao: 'Lista sem repetição, com quantas vezes cada um falhou. Pronta para limpar a base.',
    unidade: ['número', 'números'],
  },
  modelos: {
    titulo: 'Modelos mais disparados',
    descricao: 'Ranking com tipo, enviadas, entregues, lidas, falhas e custo de cada modelo.',
    unidade: ['modelo', 'modelos'],
  },
}

// Rótulo do período que vai no PDF: o nome do preset e as datas de fato.
function rotuloDoPeriodo(periodLabel, range) {
  if (!range || !range.startDay || !range.endDayInclusive) return periodLabel
  const br = (d) => `${d.slice(8, 10)}/${d.slice(5, 7)}`
  const datas = range.startDay === range.endDayInclusive
    ? `${br(range.startDay)}/${range.startDay.slice(0, 4)}`
    : `${br(range.startDay)} a ${br(range.endDayInclusive)}/${range.endDayInclusive.slice(0, 4)}`
  return periodLabel && !periodLabel.includes('/') ? `${periodLabel} · ${datas}` : datas
}

function Conteudo({ api, secret, provider, metaOficial, range, periodLabel, tipoInicial, onFechar }) {
  const theme = useTheme()
  const [tipo, setTipo] = useState(tipoInicial || 'erros')
  const [erros, setErros] = useState(null)
  const [escolhidos, setEscolhidos] = useState(null)
  const [contagem, setContagem] = useState({})
  const [gerando, setGerando] = useState(false)
  const [pronto, setPronto] = useState(false)
  const [falha, setFalha] = useState('')

  const periodo = { dateBegin: range.dateBegin, dateEnd: range.dateEnd }
  const motivos = useMemo(() => (erros ? agruparMotivos(erros.motivos, provider) : []), [erros, provider])

  // Resumo das falhas: alimenta os chips e já traz o total de tentativas.
  useEffect(() => {
    let vivo = true
    hipersendService.notificacoesInteligentes.getRelatorioErros(api, secret, periodo)
      .then((r) => { if (vivo) setErros(r || { motivos: [], porDia: [], tentativas: {} }) })
      .catch(() => { if (vivo) setErros({ motivos: [], porDia: [], tentativas: {} }) })
    return () => { vivo = false }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Por padrão vão todos os motivos.
  useEffect(() => {
    if (motivos.length && escolhidos === null) setEscolhidos(new Set(motivos.map((m) => m.id)))
  }, [motivos, escolhidos])

  // Contagem das outras opções, pra o botão dizer o que vai sair.
  useEffect(() => {
    if (!metaOficial || contagem[tipo] !== undefined || tipo === 'erros') return
    const busca = tipo === 'inexistentes'
      ? hipersendService.notificacoesInteligentes.getRelatorioInexistentes(api, secret, periodo).then((r) => r?.totais?.numeros || 0)
      : hipersendService.notificacoesInteligentes.getRelatorioModelos(api, secret, periodo).then((r) => r?.modelos?.length || 0)
    busca.then((n) => setContagem((c) => ({ ...c, [tipo]: n }))).catch(() => setContagem((c) => ({ ...c, [tipo]: null })))
  }, [tipo]) // eslint-disable-line react-hooks/exhaustive-deps

  const selecionados = motivos.filter((m) => escolhidos && escolhidos.has(m.id))
  const quantos = tipo === 'erros' ? selecionados.reduce((a, m) => a + m.total, 0) : contagem[tipo]
  const unidade = OPCOES[tipo].unidade[quantos === 1 ? 0 : 1]

  const alternarMotivo = (id) => {
    setPronto(false)
    setEscolhidos((atual) => {
      const novo = new Set(atual || [])
      if (novo.has(id)) novo.delete(id)
      else novo.add(id)
      return novo
    })
  }

  const baixar = async () => {
    setGerando(true)
    setFalha('')
    setPronto(false)
    try {
      const gerador = await carregarGerador()
      const base = {
        instancia: api,
        periodoLabel: rotuloDoPeriodo(periodLabel, range),
        geradoEm: new Date(),
      }
      if (tipo === 'erros') {
        const chaves = selecionados.flatMap((m) => m.chaves)
        const lista = await hipersendService.notificacoesInteligentes.getMensagensComFalha(api, secret, { ...periodo, chaves })
        await gerador.baixarPdfFalhas({
          ...base,
          metaOficial,
          tentativas: erros.tentativas || {},
          motivos,
          motivosEscolhidos: selecionados,
          dias: daysBetween(range.startDay, range.endDayInclusive),
          porDia: erros.porDia || [],
          lista: lista || { total: 0, mensagens: [] },
        })
      } else if (tipo === 'inexistentes') {
        const r = await hipersendService.notificacoesInteligentes.getRelatorioInexistentes(api, secret, periodo)
        await gerador.baixarPdfInexistentes({
          ...base,
          numeros: r?.numeros || [],
          totais: r?.totais || { numeros: 0, tentativas: 0, repetidos: 0 },
          tentativasPeriodo: r?.tentativasPeriodo,
          limite: r?.limite,
        })
      } else {
        const r = await hipersendService.notificacoesInteligentes.getRelatorioModelos(api, secret, periodo)
        await gerador.baixarPdfModelos({ ...base, modelos: r?.modelos || [], totais: r?.totais || { enviadas: 0 } })
      }
      setPronto(true)
    } catch (e) {
      console.error(e)
      setFalha('Não foi possível gerar o PDF agora. Confira a conexão e tente de novo.')
    } finally {
      setGerando(false)
    }
  }

  const opcoes = metaOficial ? ['erros', 'inexistentes', 'modelos'] : ['erros']
  const borda = theme.isDark ? '#334155' : '#e2e8f0'
  const pri = theme.palette.primary.main
  const vermelho = '#eb3c3c'

  let rotuloBotao = 'Baixar PDF'
  if (gerando) rotuloBotao = 'Gerando PDF…'
  else if (pronto) rotuloBotao = 'PDF baixado'
  else if (tipo === 'erros' && erros && quantos === 0) rotuloBotao = motivos.length ? 'Escolha ao menos um tipo' : 'Nenhuma falha no período'
  else if (typeof quantos === 'number') rotuloBotao = `Baixar PDF · ${fmt(quantos)} ${unidade}`

  const semNada = tipo === 'erros' && erros && quantos === 0 && motivos.length > 0

  return (
    <div className="p-4 w-full md:w-[420px]">
      <div className="flex items-center justify-between">
        <Text className="text-[15px] font-extrabold">Exportar relatório</Text>
        <button type="button" aria-label="Fechar" onClick={onFechar} className="opacity-60">
          <CloseIcon style={{ fontSize: 20 }} />
        </button>
      </div>
      <Text className="text-xs opacity-60 mt-0.5 mb-2">Período: {rotuloDoPeriodo(periodLabel, range)}</Text>

      {opcoes.map((k) => {
        const ativo = tipo === k
        return (
          <div
            key={k}
            role="radio"
            aria-checked={ativo}
            tabIndex={0}
            onClick={() => { setTipo(k); setPronto(false) }}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setTipo(k); setPronto(false) } }}
            className="mt-2 rounded-xl px-3 py-2.5 cursor-pointer outline-none focus-visible:ring-2"
            style={{ border: `1px solid ${ativo ? pri : borda}`, background: ativo ? `${pri}12` : 'transparent' }}
          >
            <div className="flex items-center gap-2.5 text-[13px] font-bold">
              <span
                className="shrink-0 rounded-full"
                style={{ width: 14, height: 14, border: ativo ? `4px solid ${pri}` : `2px solid ${borda}` }}
              />
              {OPCOES[k].titulo}
            </div>
            <Text className="text-[11.5px] opacity-60 mt-1 ml-6 leading-snug">{OPCOES[k].descricao}</Text>
            {k === 'erros' && ativo && (
              <div className="mt-2 ml-6 flex flex-wrap gap-1.5">
                {!erros && <CircularProgressCustom size={16} />}
                {erros && motivos.length === 0 && <Text className="text-[11.5px] opacity-60">Nenhuma falha no período.</Text>}
                {motivos.map((m) => {
                  const on = escolhidos && escolhidos.has(m.id)
                  return (
                    <button
                      key={m.id}
                      type="button"
                      aria-pressed={!!on}
                      onClick={(e) => { e.stopPropagation(); alternarMotivo(m.id) }}
                      className="rounded-lg px-2 py-1 text-[11px] font-semibold text-left"
                      style={{
                        border: `1px solid ${on ? vermelho : borda}`,
                        color: on ? vermelho : 'inherit',
                        background: on ? `${vermelho}14` : 'transparent',
                      }}
                    >
                      {m.titulo}
                      <b className="ml-1 opacity-60">{fmt(m.total)}</b>
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        )
      })}

      {falha && <Text className="text-xs mt-3" style={{ color: vermelho }}>{falha}</Text>}

      <div className="mt-4 flex items-center justify-between gap-3">
        <Text className="text-[11px] opacity-50">PDF pronto para enviar ao gestor</Text>
        <Button
          onClick={baixar}
          disabled={gerando || semNada || (tipo === 'erros' && (!erros || motivos.length === 0)) || quantos === 0}
          variant="contained"
          disableElevation
          startIcon={gerando ? <CircularProgressCustom size={14} /> : pronto ? <CheckIcon /> : <PictureAsPdfOutlinedIcon />}
          sx={{
            borderRadius: '9px',
            textTransform: 'none',
            fontWeight: 800,
            fontSize: 12.5,
            ...(pronto ? { backgroundColor: '#059669', '&:hover': { backgroundColor: '#047857' } } : {}),
          }}
        >
          {rotuloBotao}
        </Button>
      </div>
    </div>
  )
}

// Popover ancorado no botão "Exportar" no computador; painel de baixo no celular.
export default function PainelExportar({ aberto, ancora, onFechar, ...props }) {
  const theme = useTheme()
  const celular = useMediaQuery(theme.breakpoints.down('md'))

  if (celular) {
    return (
      <Drawer
        anchor="bottom"
        open={aberto}
        onClose={onFechar}
        PaperProps={{ sx: { borderTopLeftRadius: 18, borderTopRightRadius: 18 } }}
      >
        {aberto && <Conteudo {...props} onFechar={onFechar} />}
      </Drawer>
    )
  }

  return (
    <Popover
      open={aberto}
      anchorEl={ancora}
      onClose={onFechar}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      PaperProps={{ sx: { borderRadius: '14px', mt: 1 } }}
    >
      {aberto && <Conteudo {...props} onFechar={onFechar} />}
    </Popover>
  )
}
